import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRODUCTS, PRODUCT_CATEGORIES, PRODUCT_TAGS } from '@/mocks/catalog';
import type { ProductCategoryId, ProductTagId } from '@/types/domain';
import ProductCard from './ProductCard';

export default function GarageSystemsSection() {
  const { t } = useTranslation();
  const [category, setCategory] = useState<ProductCategoryId | 'all'>('all');
  const [tag, setTag] = useState<ProductTagId | 'all'>('all');

  const filtered = PRODUCTS.filter(
    (p) =>
      (category === 'all' || p.category === category) &&
      (tag === 'all' || p.tags.includes(tag)),
  );

  return (
    <section id="garaj" className="bg-background-100 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-3xl text-center">
          <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
          <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl lg:text-5xl">
            {t('catalog.title')}
          </h2>
        </div>

        {/* Category tabs */}
        <div className="reveal reveal-delay-1 mt-10 flex flex-wrap items-center justify-center gap-3">
          <div className="inline-flex flex-wrap justify-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                category === 'all'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-700 hover:bg-primary-100/70'
              }`}
            >
              {t('catalog.all')}
            </button>
            {PRODUCT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  category === c.id
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-700 hover:bg-primary-100/70'
                }`}
              >
                {t(c.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Tag filters */}
        <div className="reveal reveal-delay-2 mt-5 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setTag('all')}
            className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
              tag === 'all'
                ? 'border-secondary-500 bg-secondary-500 text-background-50'
                : 'border-background-300 text-foreground-600 hover:border-secondary-400'
            }`}
          >
            {t('catalog.all')}
          </button>
          {PRODUCT_TAGS.map((tg) => (
            <button
              key={tg.id}
              type="button"
              onClick={() => setTag(tg.id)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                tag === tg.id
                  ? 'border-secondary-500 bg-secondary-500 text-background-50'
                  : 'border-background-300 text-foreground-600 hover:border-secondary-400'
              }`}
            >
              {t(tg.labelKey)}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <p className="mt-14 text-center text-sm text-foreground-500">{t('catalog.empty')}</p>
        )}
      </div>
    </section>
  );
}